import React, { useEffect, useState, useRef } from 'react';
import { RootState } from 'src/store/reducers/state';
import { useParams } from 'react-router-dom';
import { useEventActions } from 'src/store/event/actions';
import { useDispatch, useSelector } from 'react-redux';
import {
    EventRegistrationImagesViewModel,
    PatchJsonModel,
    PatchJsonData
} from 'src/models/eventModel';
import { useForm } from 'react-hook-form';
import SelectNewThumbModal from '../../videos/components/SelectNewThumbModal';
import { uploadImageToS3WithReturnData } from 'src/components/utilities/utils';
const DEFAULT_COLORS = {
    headerBackgroundColor: '#FFFFFF',
    bannerBackgroundColor: '#FFFFFF',
    bodyBackgroundColor: '#FFFFFF',
    h1Color: '#212529',
    formHeadingFontColor: '#212529',
    formLabelFontColor: '#212529',
    formButtonColor: '#007BFF'
};
const SectionCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
    title,
    subtitle,
    children
}) => (
    <div className="bg-indigo-50/50 rounded-lg overflow-hidden">
        <div className="px-4 pt-3 pb-2">
            <h3 className="text-sm font-semibold text-gray-900 m-0">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5 m-0">{subtitle}</p>}
        </div>
        <div className="px-4 pb-4">{children}</div>
    </div>
);

const PlaceholderIcon: React.FC = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="text-gray-300">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
    </svg>
);

const UploadBox: React.FC<{ hint?: string; onClick: () => void }> = ({ hint, onClick }) => (
    <div
        onClick={onClick}
        className="border-2 border-dashed border-gray-300 rounded-lg bg-white flex flex-col items-center justify-center text-center py-6 px-4 cursor-pointer hover:border-indigo-300 transition-colors">
        <PlaceholderIcon />
        <p className="hidden sm:block text-xs text-gray-500 m-0 mt-2">Drag and drop an image here</p>
        <p className="text-xs text-indigo-600 underline cursor-pointer m-0 mt-2 sm:mt-0.5">
            <span className="sm:hidden">Tap to upload an image</span>
            <span className="hidden sm:inline">or click to upload</span>
        </p>
        {hint && <p className="text-[11px] text-gray-400 mt-2 m-0">{hint}</p>}
    </div>
);

const UploadActions: React.FC<{ onUpload: () => void; onRemove: () => void }> = ({
    onUpload,
    onRemove
}) => (
    <div className="flex items-center gap-2 mt-3">
        <button
            type="button"
            onClick={onUpload}
            className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-4 py-2">
            Upload
        </button>
        <button
            type="button"
            onClick={onRemove}
            className="text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg px-4 py-2 hover:bg-gray-50">
            Remove
        </button>
    </div>
);

const PreviewBox: React.FC<{ src: string | null; heightClass?: string }> = ({
    src,
    heightClass = 'h-[120px]'
}) => (
    <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Preview</label>
        <div
            className={`rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center ${heightClass} overflow-hidden`}>
            {src ? (
                <img
                    src={src}
                    className="max-h-full max-w-full"
                    style={{ objectFit: 'contain', width: 'auto', height: 'auto' }}
                />
            ) : (
                <NoImagePlaceholder />
            )}
        </div>
    </div>
);

const ColorField: React.FC<{
    label: string;
    checked: boolean | null;
    hexValue: string | null;
    defaultColor: string;
    registerProps: any;
    onCheckedChange: (useDefault: boolean) => void;
    onHexChange: (value: string) => void;
}> = ({ label, checked, hexValue, defaultColor, registerProps, onCheckedChange, onHexChange }) => {
    const isDefault = checked !== false;
    const groupName = `color-${label.replace(/\s+/g, '-').toLowerCase()}`;

    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
            <div className="space-y-2">
                {/* Use default */}
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="radio"
                        name={groupName}
                        checked={isDefault}
                        onChange={() => onCheckedChange(true)}
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-400"
                    />
                    <span className="text-xs text-gray-700 w-24 flex-shrink-0">Use default</span>
                    <span
                        className="w-7 h-7 rounded-md border border-gray-200 flex-shrink-0"
                        style={{ backgroundColor: defaultColor }}
                    />
                    <span className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
                        {defaultColor}
                    </span>
                    <span className="text-xs text-gray-400" style={{ fontSize: '11px' }}>(Default)</span>
                </label>

                {/* Custom colour */}
                <div
                    className="flex items-center gap-2 cursor-pointer min-w-0"
                    onClick={() => isDefault && onCheckedChange(false)}>
                    <input
                        type="radio"
                        name={groupName}
                        checked={!isDefault}
                        onChange={() => onCheckedChange(false)}
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-400"
                    />
                    <span className="text-xs text-gray-700 w-24 flex-shrink-0">Custom colour</span>
                    <input
                        type="color"
                        {...registerProps}
                        value={!isDefault && hexValue ? hexValue : '#FFFFFF'}
                        onChange={(e) => onHexChange(e.target.value)}
                        className="w-7 h-7 rounded-md border border-gray-200 flex-shrink-0 p-0 cursor-pointer"
                    />
                    <input
                        type="text"
                        value={!isDefault ? hexValue || '' : ''}
                        onChange={(e) => onHexChange(e.target.value)}
                        placeholder="#000000"
                        className="w-full min-w-0 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                    />
                </div>
            </div>
        </div>
    );
};
const NoImagePlaceholder: React.FC = () => (
    <div className="flex flex-col items-center justify-center text-gray-400 gap-1">
        <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-gray-400">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </svg>
        <span className="text-[10px] font-semibold tracking-wide uppercase">No Image Available</span>
    </div>
);
const ImagesAndStyle = ({ setCurrentStep }: any) => {
    const dispatch = useDispatch();
    const eventActions = useEventActions(dispatch);
    const inputEventRegistrationImageFile = useRef<HTMLInputElement>(null);
    const inputBannerImageFile = useRef<HTMLInputElement>(null);
    const inputBodyBackgroundImageFile = useRef<HTMLInputElement>(null);
    const { eventId } = useParams<string>();
    const [eventRegistrationLogo, setEventRegistrationLogo] = useState<any>(null);
    const [bannerImage, setBannerImage] = useState<any>(null);
    const [bodyBackgroundImage, setBodyBackgroundImage] = useState<any>(null);
    const [eventUploadLogo, setEventUploadLogo] = useState<any>(null);
    const [bannerUploadLogo, setBannerUploadLogo] = useState<any>(null);
    const [bodyBackgroundUploadImage, setBodyBackgroundUploadImage] = useState<any>(null);
    const [headerBackgroundColor, setHeaderBackgroundColor] = useState<any>(null);
    const [bannerBackgroundColor, setBannerBackgroundColor] = useState<any>(null);
    const [bodyBackgroundColor, setBodyBackgroundColor] = useState<any>(null);
    const [formButtonColor, setFormButtonColor] = useState<any>(null);
    const [logoWidth, setLogoWidth] = useState<any>(0);
    const [h1Color, setH1Color] = useState<any>(null);
    const [formHeadingFontColor, setFormHeadingFontColor] = useState<any>(null);
    const [formLabelFontColor, setFormLabelFontColor] = useState<any>(null);
    const [eventLogoUploadReturnData, setEventLogoUploadReturnData] = useState<any>(null);
    const [bannerImageUploadReturnData, setBannerImageUploadReturnData] = useState<any>(null);
    const [bodyImageUploadReturnData, setBodyImageUploadReturnData] = useState<any>(null);
    const [headerBackgroundColorChecked, setHeaderBackgroundColorChecked] = useState<any>(null);
    const [bannerBackgroundColorChecked, setBannerBackgroundColorChecked] = useState<any>(null);
    const [bodyBackgroundColorChecked, setBodyBackgroundColorChecked] = useState<any>(null);
    const [h1ColorChecked, seth1ColorChecked] = useState<any>(null);
    const [formHeadingFontColorChecked, setFormHeadingFontColorChecked] = useState<any>(null);
    const [formLabelFontColorChecked, setFormLabelFontColorChecked] = useState<any>(null);
    const [formButtonColorChecked, setFormButtonColorChecked] = useState<any>(null);
    const [displayHeaderChecked, setDisplayHeaderChecked] = useState<boolean>(true);
    const { register, handleSubmit, reset, setValue } = useForm<EventRegistrationImagesViewModel>();
    const handleSelectNewClick = (inputName: any) => {
        if (inputName.current) {
            inputName.current.click();
        }
    };
    const { eventRegistrationImagesView, isEventSaved } = useSelector((state: RootState) => {
        return {
            eventRegistrationImagesView: state.event.singleEvent.eventRegistrationField
                ? state.event.singleEvent.eventRegistrationField.eventRegistrationImagesViewModel
                : null,
            isEventSaved: state.event.isEventSaved,
            isEventUpdated: state.event.isEventUpdated
        };
    });

    useEffect(() => {
        if (isEventSaved === true) {
            reset({
                uploadBannerUrl: '',
                uploadBannerUrlS3: '',
                uploadLogoUrl: '',
                uploadLogoUrlS3: '',
                emailOnRegistration: true,
                emailSubject: '',
                emailBody: '',
                displayHeader: true,
                headerBackgroundColor: '',
                bannerBackgroundColor: '',
                bodyBackgroundColor: '',
                formButtonColor: '',
                h1Color: '',
                customFontLoaderCode: '',
                headingFontFamilyCss: '',
                sendICSAttachment: true,
                logoWidth: '',
                customCSS: '',
                formHeadingFontSize: 0,
                formHeadingFontColor: '',
                formHeadingFontFamily: '',
                formLabelFontSize: 0,
                formLabelFontColor: '',
                formLabelFontFamily: ''
            });
        }
    }, [isEventSaved]);
    useEffect(() => {
        if (eventId) {
            eventActions.getSingleEventRequest(eventId)
        }
    }, [])
    useEffect(() => {
        if (eventRegistrationImagesView) {
            reset(eventRegistrationImagesView);
            setEventRegistrationLogo(
                eventRegistrationImagesView.uploadLogoUrl
                    ? eventRegistrationImagesView.uploadLogoUrl
                    : null
            );
            setEventLogoUploadReturnData(eventRegistrationImagesView.uploadLogoUrl);
            setBannerImage(
                eventRegistrationImagesView.uploadBannerUrl
                    ? eventRegistrationImagesView.uploadBannerUrl
                    : null
            );
            setBannerImageUploadReturnData(eventRegistrationImagesView.uploadBannerUrl);
            setBodyBackgroundImage(
                eventRegistrationImagesView.bodyBackgroundImageUrl
                    ? eventRegistrationImagesView.bodyBackgroundImageUrl
                    : null
            );
            setBodyImageUploadReturnData(eventRegistrationImagesView.bodyBackgroundImageUrl);
            setLogoWidth(eventRegistrationImagesView.logoWidth);
            setHeaderBackgroundColor(eventRegistrationImagesView.headerBackgroundColor);
            setHeaderBackgroundColorChecked(
                eventRegistrationImagesView.headerBackgroundColor == null ? true : false
            );
            setBannerBackgroundColor(eventRegistrationImagesView.bannerBackgroundColor);
            setBannerBackgroundColorChecked(
                eventRegistrationImagesView.bannerBackgroundColor == null ? true : false
            );
            setBodyBackgroundColor(eventRegistrationImagesView.bodyBackgroundColor);
            setBodyBackgroundColorChecked(
                eventRegistrationImagesView.bodyBackgroundColor == null ? true : false
            );
            setFormButtonColor(eventRegistrationImagesView.formButtonColor);
            setFormButtonColorChecked(eventRegistrationImagesView.formButtonColor == null ? true : false);
            setH1Color(eventRegistrationImagesView.h1Color);
            seth1ColorChecked(eventRegistrationImagesView.h1Color == null ? true : false);
            setFormHeadingFontColor(eventRegistrationImagesView.formHeadingFontColor);
            setFormHeadingFontColorChecked(
                eventRegistrationImagesView.formHeadingFontColor == null ? true : false
            );
            setFormLabelFontColor(eventRegistrationImagesView.formLabelFontColor);
            setFormLabelFontColorChecked(
                eventRegistrationImagesView.formLabelFontColor == null ? true : false
            );
            setDisplayHeaderChecked(
                eventRegistrationImagesView.displayHeader === null ||
                    eventRegistrationImagesView.displayHeader === undefined
                    ? true
                    : eventRegistrationImagesView.displayHeader
            );
        }
        if (eventId?.length == 0) {
            reset({
                uploadBannerUrl: '',
                uploadBannerUrlS3: '',
                uploadLogoUrl: '',
                uploadLogoUrlS3: '',
                emailOnRegistration: true,
                emailSubject: '',
                emailBody: '',
                displayHeader: true,
                headerBackgroundColor: '',
                bannerBackgroundColor: '',
                bodyBackgroundColor: '',
                formButtonColor: '',
                h1Color: '',
                customFontLoaderCode: '',
                headingFontFamilyCss: '',
                sendICSAttachment: true,
                logoWidth: '',
                customCSS: '',
                formHeadingFontSize: 0,
                formHeadingFontColor: '',
                formHeadingFontFamily: '',
                formLabelFontSize: 0,
                formLabelFontColor: '',
                formLabelFontFamily: ''
            });
            setDisplayHeaderChecked(true);
        }
    }, [eventRegistrationImagesView]);

    const onImageChange = (event: any, setVariable: any, setFiletype: any) => {
        const reader = new FileReader();
        const file = event.target.files[0];
        setFiletype(file);
        reader.onloadend = () => {
            setVariable(reader.result);
        };
        reader.readAsDataURL(file);
    };
    const onSubmit = async (data: any) => {
        const payloadData = {
            id: data.id,
            eventId: data.eventId,
            displayHeader: data.displayHeader,
            headerBackgroundColor: data.headerBackgroundColor,
            bannerBackgroundColor: data.bannerBackgroundColor,
            bodyBackgroundColor: data.bodyBackgroundColor,
            formButtonColor: data.formButtonColor,
            h1Color: data.h1Color,
            customFontLoaderCode: data.customFontLoaderCode,
            headingFontFamilyCss: data.headingFontFamilyCss,
            logoWidth: data.logoWidth,
            customCSS: data.customCSS,
            formHeadingFontSize: data.formHeadingFontSize,
            formHeadingFontColor: data.formHeadingFontColor,
            formHeadingFontFamily: data.formHeadingFontFamily,
            formLabelFontSize: data.formLabelFontSize,
            formLabelFontColor: data.formLabelFontColor,
            formLabelFontFamily: data.formLabelFontFamily,
            uploadLogoUrl: eventLogoUploadReturnData,
            uploadBannerUrl: bannerImageUploadReturnData,
            bodyBackgroundImageUrl: bodyImageUploadReturnData
        };
        eventActions.updateEventLayoutRequest(payloadData);
        setCurrentStep(6)
    };

    const removeSelectedImage = (selectedCurrentImage: any) => {
        setEventLogoUploadReturnData(null);
        selectedCurrentImage(null);
    };

    const removeBannerImage = (selectedCurrentImage: any) => {
        setBannerImageUploadReturnData(null);
        selectedCurrentImage(null);
    };

    const removeBackgroundImage = (selectedCurrentImage: any) => {
        setBodyImageUploadReturnData(null);
        selectedCurrentImage(null);
    };
    const handleChangeColor = (useDefault: boolean, setColorCodeVariable: any, from: string) => {
        const checkedSetters: Record<string, (v: boolean) => void> = {
            headerBackgroundColor: setHeaderBackgroundColorChecked,
            bannerBackgroundColor: setBannerBackgroundColorChecked,
            bodyBackgroundColor: setBodyBackgroundColorChecked,
            h1Color: seth1ColorChecked,
            formHeadingFontColor: setFormHeadingFontColorChecked,
            formLabelFontColor: setFormLabelFontColorChecked,
            formButtonColor: setFormButtonColorChecked
        };
        checkedSetters[from]?.(useDefault);

        const value = useDefault ? null : '#000000';
        setColorCodeVariable(value);
        setValue(from as any, value as any);
    };
    const isBackgroundColorTransparent = (checkedValue: any) => {
        if (checkedValue === null) return true;
        else return false;
    };
    const handleLogoImageUpload = async (selectedFile: any) => {
        if (selectedFile) {
            const reader = new FileReader();
            const file = selectedFile[0];
            reader.onloadend = () => {
                setEventRegistrationLogo(reader.result);
            };
            setEventUploadLogo(selectedFile[0].name);
            reader.readAsDataURL(file);
            if (selectedFile) {
                await uploadImageToS3WithReturnData(
                    selectedFile[0],
                    `v2/event/upload/logo?eventId=${eventId}`,
                    (imageurl: any) => {
                        setEventLogoUploadReturnData(imageurl);
                    }
                );
            }
        }
    };

    const handleBannerImageUpload = async (selectedFile: any) => {
        if (selectedFile) {
            const reader = new FileReader();
            const file = selectedFile[0];
            reader.onloadend = () => {
                setBannerImage(reader.result);
            };
            setBannerUploadLogo(selectedFile[0].name);
            reader.readAsDataURL(file);
            if (selectedFile) {
                await uploadImageToS3WithReturnData(
                    selectedFile[0],
                    `v2/event/upload/bannerimage?eventId=${eventId}`,
                    (imageurl: any) => {
                        setBannerImageUploadReturnData(imageurl);
                    }
                );
            }
        }
    };
    const handleBodyBackgroundImageUpload = async (selectedFile: any) => {
        if (selectedFile) {
            const reader = new FileReader();
            const file = selectedFile[0];
            reader.onloadend = () => {
                setBodyBackgroundImage(reader.result);
            };
            setBodyBackgroundUploadImage(selectedFile[0].name);
            reader.readAsDataURL(file);
            if (selectedFile) {
                await uploadImageToS3WithReturnData(
                    selectedFile[0],
                    `v2/event/upload/backgroundimage?eventId=${eventId}`,
                    (imageurl: any) => {
                        setBodyImageUploadReturnData(imageurl);
                    }
                );
            }
        }
    };

    return (
        <div className='flex-1 min-w-0 w-full'>
            <main className="flex-1 flex flex-col min-w-0">
                <div className="bg-white rounded-xl border border-gray-200 flex-1 flex flex-col">
                    <div className="px-4 sm:px-8 pt-5 sm:pt-6 pb-4 border-b border-gray-100">
                        <h2 className="text-lg font-semibold text-gray-900 m-0">Images &amp; Style</h2>
                        <p className="text-sm text-gray-400 mt-1">Customize the look and feel of your event page.</p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="px-4 sm:px-8 py-5 sm:py-6 space-y-5">
                            {/* Event Header & Branding */}
                            <SectionCard
                                title="Event Header & Branding"
                                subtitle="These images and settings will be used at the top of your event page.">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Event Registration Logo
                                        </label>
                                        <UploadBox
                                            hint="Recommended size: 400 x 100px (PNG or JPG)"
                                            onClick={() => handleSelectNewClick(inputEventRegistrationImageFile)}
                                        />
                                        <input
                                            ref={inputEventRegistrationImageFile}
                                            type="file"
                                            onChange={(e) => handleLogoImageUpload(e.target.files)}
                                            className="hidden"
                                        />
                                        <UploadActions
                                            onUpload={() => handleSelectNewClick(inputEventRegistrationImageFile)}
                                            onRemove={() => removeSelectedImage(setEventRegistrationLogo)}
                                        />
                                    </div>
                                    <PreviewBox src={eventRegistrationLogo} />
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-5 items-start">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Logo Width</label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="range"
                                                min={0}
                                                max={500}
                                                id="logoWidth"
                                                {...register('logoWidth', {
                                                    onChange: (e: any) => {
                                                        setLogoWidth(e.target.value);
                                                    }
                                                })}
                                                value={logoWidth}
                                                className="flex-1 min-w-0 accent-indigo-600"
                                            />
                                            <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 w-20" style={{ width: "100px" }}>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={500}
                                                    value={logoWidth}
                                                    onChange={(e) => {
                                                        setLogoWidth(e.target.value);
                                                        setValue('logoWidth', e.target.value);
                                                    }}
                                                    className="w-full focus:outline-none"
                                                    style={{ width: "70px" }}
                                                />
                                                <span className="text-gray-400">px</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='form-switch'>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                                            Display Video Channel Header
                                        </label>
                                        <input
                                            type="checkbox"
                                            className="form-check-input switch-btn"
                                            {...register('displayHeader')}
                                        />

                                    </div>

                                    <ColorField
                                        label="Header Background Color"
                                        defaultColor={DEFAULT_COLORS.headerBackgroundColor}
                                        checked={headerBackgroundColorChecked}
                                        hexValue={headerBackgroundColor}
                                        registerProps={register('headerBackgroundColor')}
                                        onCheckedChange={(checked) =>
                                            handleChangeColor(checked, setHeaderBackgroundColor, 'headerBackgroundColor')
                                        }
                                        onHexChange={(value) => {
                                            setHeaderBackgroundColor(value); setValue('headerBackgroundColor', value);
                                        }}
                                    />
                                </div>
                            </SectionCard>

                            {/* Banner Image */}
                            <SectionCard
                                title="Banner Image"
                                subtitle="This image will be displayed below the header on the event page.">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Banner Image{' '}
                                            <span className="text-xs font-normal text-gray-400">
                                                (Recommended size: 1400 x 400px)
                                            </span>
                                        </label>
                                        <UploadBox onClick={() => handleSelectNewClick(inputBannerImageFile)} />
                                        <input
                                            ref={inputBannerImageFile}
                                            type="file"
                                            onChange={(e) => handleBannerImageUpload(e.target.files)}
                                            className="hidden"
                                        />
                                        <UploadActions
                                            onUpload={() => handleSelectNewClick(inputBannerImageFile)}
                                            onRemove={() => removeBannerImage(setBannerImage)}
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <PreviewBox src={bannerImage} heightClass="h-[140px]" />
                                        <div className="w-full sm:max-w-[220px]">
                                            <ColorField
                                                label="Banner Background Color"
                                                defaultColor={DEFAULT_COLORS.bannerBackgroundColor}
                                                checked={bannerBackgroundColorChecked}
                                                hexValue={bannerBackgroundColor}
                                                registerProps={register('bannerBackgroundColor')}
                                                onCheckedChange={(checked) =>
                                                    handleChangeColor(checked, setBannerBackgroundColor, 'bannerBackgroundColor')
                                                }
                                                onHexChange={(value) => {
                                                    setBannerBackgroundColor(value);
                                                    setValue('bannerBackgroundColor', value);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Page Body Style*/}
                            <SectionCard
                                title="Page Body Style"
                                subtitle="Customize the colours and background for the main content area.">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <div className="w-full sm:max-w-[220px]">
                                        <ColorField
                                            label="Body Background Color"
                                            defaultColor={DEFAULT_COLORS.bodyBackgroundColor}
                                            checked={bodyBackgroundColorChecked}
                                            hexValue={bodyBackgroundColor}
                                            registerProps={register('bodyBackgroundColor')}
                                            onCheckedChange={(checked) =>
                                                handleChangeColor(checked, setBodyBackgroundColor, 'bodyBackgroundColor')
                                            }
                                            onHexChange={(value) => {
                                                setBodyBackgroundColor(value);
                                                setValue('bodyBackgroundColor', value);
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Body Background Image
                                        </label>
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3">
                                            <div className="flex-1 min-w-0">
                                                <UploadBox onClick={() => handleSelectNewClick(inputBodyBackgroundImageFile)} />
                                                <input
                                                    ref={inputBodyBackgroundImageFile}
                                                    type="file"
                                                    onChange={(e) => handleBodyBackgroundImageUpload(e.target.files)}
                                                    className="hidden"
                                                />
                                            </div>
                                            <div className="flex flex-row sm:flex-col gap-2 sm:pt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSelectNewClick(inputBodyBackgroundImageFile)}
                                                    className="text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-4 py-2">
                                                    Upload
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeBackgroundImage(setBodyBackgroundImage)}
                                                    className="text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg px-4 py-2 hover:bg-gray-50">
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                        {bodyBackgroundImage && (
                                            <div className="mt-3">
                                                <PreviewBox src={bodyBackgroundImage} heightClass="h-[90px]" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Heading / H1 style */}
                            <SectionCard
                                title="Heading Style"
                                subtitle="Set the colour and font family used for the page heading.">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <ColorField
                                        label="Heading (H1) Color"
                                        defaultColor={DEFAULT_COLORS.h1Color}
                                        checked={h1ColorChecked}
                                        hexValue={h1Color}
                                        registerProps={register('h1Color')}
                                        onCheckedChange={(checked) => handleChangeColor(checked, setH1Color, 'h1Color')}
                                        onHexChange={(value) => {
                                            setH1Color(value);
                                            setValue('h1Color', value);
                                        }}
                                    />
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Heading Font Family Css
                                        </label>
                                        <input
                                            type="text"
                                            {...register('headingFontFamilyCss')}
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1">
                                            Custom Font Loader Code
                                        </label>
                                        <input
                                            type="text"
                                            {...register('customFontLoaderCode')}
                                            placeholder=""
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                        />
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Text & Form Style */}
                            <SectionCard
                                title="Text & Form Style"
                                subtitle="Set the colours, fonts and sizes used for the registration form.">
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Form Heading Font Size
                                        </label>
                                        <select
                                            id="formHeadingFontSize"
                                            {...register('formHeadingFontSize')}
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-100">
                                            {Array.from({ length: 500 }, (v, i) => i + 1).map((item) => (
                                                <option key={item} value={item}>{`${item}px`}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <ColorField
                                        label="Form Heading Font Color"
                                        defaultColor={DEFAULT_COLORS.formHeadingFontColor}
                                        checked={formHeadingFontColorChecked}
                                        hexValue={formHeadingFontColor}
                                        registerProps={register('formHeadingFontColor')}
                                        onCheckedChange={(checked) =>
                                            handleChangeColor(checked, setFormHeadingFontColor, 'formHeadingFontColor')
                                        }
                                        onHexChange={(value) => {
                                            setFormHeadingFontColor(value);
                                            setValue('formHeadingFontColor', value);
                                        }}
                                    />
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Form Heading Font Family
                                        </label>
                                        <input
                                            type="text"
                                            {...register('formHeadingFontFamily')}
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Form Label Font Size
                                        </label>
                                        <select
                                            {...register('formLabelFontSize')}
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-100">
                                            {Array.from({ length: 500 }, (v, i) => i + 1).map((item) => (
                                                <option key={item} value={item}>{`${item}px`}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <ColorField
                                        label="Form Label Font Color"
                                        defaultColor={DEFAULT_COLORS.formLabelFontColor}
                                        checked={formLabelFontColorChecked}
                                        hexValue={formLabelFontColor}
                                        registerProps={register('formLabelFontColor')}
                                        onCheckedChange={(checked) =>
                                            handleChangeColor(checked, setFormLabelFontColor, 'formLabelFontColor')
                                        }
                                        onHexChange={(value) => { setFormLabelFontColor(value); setValue('formLabelFontColor', value); }}
                                    />
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                            Form Label Font Family
                                        </label>
                                        <input
                                            type="text"
                                            {...register('formLabelFontFamily')}
                                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
                                        />
                                    </div>

                                    <ColorField
                                        label="Form Button Color"
                                        defaultColor={DEFAULT_COLORS.formButtonColor}
                                        checked={formButtonColorChecked}
                                        hexValue={formButtonColor}
                                        registerProps={register('formButtonColor')}
                                        onCheckedChange={(checked) =>
                                            handleChangeColor(checked, setFormButtonColor, 'formButtonColor')
                                        }
                                        onHexChange={(value) => { setFormButtonColor(value); setValue('formButtonColor', value); }}
                                    />

                                    <div className='xl:ml-5'>
                                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Button Preview</label>
                                        <button
                                            type="button"
                                            style={{
                                                backgroundColor:
                                                    formButtonColorChecked === false && formButtonColor
                                                        ? formButtonColor
                                                        : DEFAULT_COLORS.formButtonColor
                                            }}
                                            className="text-sm font-semibold text-white rounded-lg px-5 py-2.5">
                                            Register
                                        </button>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* Custom CSS*/}
                            <SectionCard
                                title="Custom CSS"
                                subtitle="Add custom CSS to further refine the appearance of your event page.">
                                <textarea
                                    {...register('customCSS')}
                                    rows={4}
                                    placeholder="/* Add your custom CSS here */"
                                    className="w-full text-sm font-mono border border-gray-200 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 resize-none"
                                />
                            </SectionCard>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-between gap-3 px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100">
                            <button
                                onClick={() => setCurrentStep(4)}
                                type="button"
                                className="flex-shrink-0 flex items-center gap-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg px-4 sm:px-5 py-2.5 hover:bg-gray-50">
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.5">
                                    <line x1="19" y1="12" x2="5" y2="12" />
                                    <polyline points="12 19 5 12 12 5" />
                                </svg>
                                Back
                            </button>
                            <button
                                type="submit"
                                className="flex-1 sm:flex-none justify-center flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg px-4 sm:px-5 py-2.5">
                                Next: Email & Notifications
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </button>
                        </div>
                    </form>
                </div>
                <SelectNewThumbModal />
            </main>
        </div>
    );
};

export default ImagesAndStyle;